import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { KpiCard } from '@/components/overview/KpiCard';
import { DistributionCard } from '@/components/overview/DistributionCard';
import { TrendChart } from '@/components/overview/TrendChart';
import { FindingsList } from '@/components/overview/FindingsList';
import { mockPatients, mockFindings, mockTrendData } from '@/data/mock';
import type { Patient } from '@/lib/types';

type Cohort = 'all' | 'adults' | 'seniors' | 'minors';
type Sex = 'all' | 'm' | 'f';

interface CohortDef {
  key: Cohort;
  label: string;
  match: (p: Patient) => boolean;
}

const COHORTS: CohortDef[] = [
  { key: 'all', label: 'Todos', match: () => true },
  { key: 'adults', label: 'Adultos · 18-64', match: (p) => p.age >= 18 && p.age <= 64 },
  { key: 'seniors', label: 'Adultos mayores · 65+', match: (p) => p.age >= 65 },
  { key: 'minors', label: 'Niños y adolescentes · <18', match: (p) => p.age < 18 },
];

const SEX_OPTS: { key: Sex; label: string; match: (p: Patient) => boolean }[] = [
  { key: 'all', label: 'Todos', match: () => true },
  { key: 'f', label: 'Femenino', match: (p) => p.gender === 'Femenino' },
  { key: 'm', label: 'Masculino', match: (p) => p.gender === 'Masculino' },
];

function isCohort(v: string | null): v is Cohort {
  return v === 'all' || v === 'adults' || v === 'seniors' || v === 'minors';
}
function isSex(v: string | null): v is Sex {
  return v === 'all' || v === 'm' || v === 'f';
}

const TODAY = new Date('2026-05-08');

function isThisMonth(iso?: string): boolean {
  if (!iso) return false;
  const d = new Date(iso);
  return d.getFullYear() === TODAY.getFullYear() && d.getMonth() === TODAY.getMonth();
}

function isWithinDays(iso: string | undefined, days: number): boolean {
  if (!iso) return false;
  const d = new Date(iso);
  const diff = (TODAY.getTime() - d.getTime()) / (1000 * 60 * 60 * 24);
  return diff >= 0 && diff <= days;
}

export function Overview() {
  const [searchParams, setSearchParams] = useSearchParams();

  const cohort: Cohort = isCohort(searchParams.get('cohort'))
    ? (searchParams.get('cohort') as Cohort)
    : 'all';
  const sex: Sex = isSex(searchParams.get('sex')) ? (searchParams.get('sex') as Sex) : 'all';

  const setParam = (key: string, value: string, defaultValue: string) => {
    const params = new URLSearchParams(searchParams);
    if (value === defaultValue) params.delete(key);
    else params.set(key, value);
    setSearchParams(params, { replace: true });
  };

  const cohortDef = COHORTS.find((c) => c.key === cohort) ?? COHORTS[0];
  const sexDef = SEX_OPTS.find((s) => s.key === sex) ?? SEX_OPTS[0];

  const filtered = useMemo(
    () => mockPatients.filter((p) => cohortDef.match(p) && sexDef.match(p)),
    [cohortDef, sexDef]
  );

  const stats = useMemo(() => {
    const total = filtered.length;
    const scheduled = filtered.filter((p) => p.appointmentState === 'programado').length;
    const attended = filtered.filter((p) => p.appointmentState === 'atendida').length;
    const totalAppts = scheduled + attended;
    const reportsThisMonth = filtered.filter((p) => isThisMonth(p.lastEvaluationAt)).length;
    const activeLast90 = filtered.filter((p) => isWithinDays(p.lastEvaluationAt, 90)).length;
    const ok = filtered.filter((p) => p.status === 'ok').length;
    const warn = filtered.filter((p) => p.status === 'warn').length;
    const crit = filtered.filter((p) => p.status === 'crit').length;
    return { total, scheduled, attended, totalAppts, reportsThisMonth, activeLast90, ok, warn, crit };
  }, [filtered]);

  const distribution = useMemo(() => {
    const t = stats.total || 1;
    return {
      ok: { count: stats.ok, pct: Math.round((stats.ok / t) * 100) },
      warn: { count: stats.warn, pct: Math.round((stats.warn / t) * 100) },
      crit: { count: stats.crit, pct: Math.round((stats.crit / t) * 100) },
    };
  }, [stats]);

  return (
    <main>
      <div className="pane-content appear">
        <header className="mb-4">
          <p className="pane-eyebrow">
            Inteligencia clínica · {stats.total} pacientes en total
          </p>
          <h1 className="pane-title">Overview</h1>
          <p className="pane-summary">
            Análisis poblacional de tu práctica. Filtra por edad, sexo y revisa
            tendencias en tiempo real.
          </p>
        </header>

        <section className="flex items-center gap-1.5 flex-wrap mb-5">
          <FilterDropdown
            label="Edad"
            value={cohortDef.label}
            isDefault={cohort === 'all'}
            options={COHORTS.map((c) => ({
              key: c.key,
              label: c.label,
              count: mockPatients.filter((p) => c.match(p) && sexDef.match(p)).length,
            }))}
            onSelect={(k) => setParam('cohort', k, 'all')}
            activeKey={cohort}
          />
          <FilterDropdown
            label="Sexo"
            value={sexDef.label}
            isDefault={sex === 'all'}
            options={SEX_OPTS.map((s) => ({
              key: s.key,
              label: s.label,
              count: mockPatients.filter((p) => cohortDef.match(p) && s.match(p)).length,
            }))}
            onSelect={(k) => setParam('sex', k, 'all')}
            activeKey={sex}
          />
          {(cohort !== 'all' || sex !== 'all') && (
            <button
              type="button"
              onClick={() => {
                setParam('cohort', 'all', 'all');
                setParam('sex', 'all', 'all');
              }}
              className="text-[11.5px] font-medium text-tertiary bg-transparent border-0 px-2 py-1 rounded-md cursor-pointer hover:text-primary transition-colors"
            >
              Limpiar filtros
            </button>
          )}
        </section>

        <div className="grid grid-cols-4 gap-2 mb-2 max-[760px]:grid-cols-2 appear-stagger">
          <KpiCard
            label="Total de pacientes"
            value={stats.total}
            delta={
              <>
                <strong>{stats.activeLast90}</strong>{' '}
                <span>activos últimos 90 d</span>
              </>
            }
          />
          <KpiCard
            label="Citas pendientes"
            value={stats.scheduled}
            valueSuffix={`/ ${stats.totalAppts}`}
            delta={
              <>
                <span>{stats.attended} atendidas · {stats.scheduled} programadas</span>
              </>
            }
          />
          <KpiCard
            label="Reportes este mes"
            value={stats.reportsThisMonth}
            delta={
              <>
                <span>{Math.round((stats.reportsThisMonth / Math.max(stats.total, 1)) * 100)}% de los pacientes evaluados</span>
              </>
            }
          />
          <KpiCard
            label="Hallazgos críticos"
            value={stats.crit}
            delta={
              <>
                <strong>{Math.round((stats.crit / Math.max(stats.total, 1)) * 100)}%</strong>{' '}
                <span>requieren acción</span>
              </>
            }
            deltaDirection={stats.crit > 0 ? 'down' : null}
          />
        </div>

        <div className="grid grid-cols-[1.3fr_1fr] gap-3 mb-2 max-[760px]:grid-cols-1">
          <DistributionCard distribution={distribution} />
          <TrendChart
            current={mockTrendData.current}
            delta={mockTrendData.delta}
            months={mockTrendData.months}
            points={mockTrendData.points}
          />
        </div>

        <h3 className="section-title">Hallazgos más frecuentes en tus pacientes</h3>
        <FindingsList findings={mockFindings} />
      </div>
    </main>
  );
}

interface FilterDropdownProps<K extends string> {
  label: string;
  value: string;
  isDefault: boolean;
  options: { key: K; label: string; count: number }[];
  onSelect: (key: K) => void;
  activeKey: K;
}

function FilterDropdown<K extends string>({
  label,
  value,
  isDefault,
  options,
  onSelect,
  activeKey,
}: FilterDropdownProps<K>) {
  return (
    <Menu as="div" className="relative">
      <MenuButton
        className={`inline-flex items-center gap-1.5 text-[12px] font-medium px-2.5 py-1.5 rounded-md border cursor-pointer transition-colors ${
          isDefault
            ? 'bg-transparent text-tertiary border-line hover:bg-soft hover:border-line-default hover:text-primary data-[open]:bg-soft data-[open]:text-primary'
            : 'bg-soft text-primary border-line-default'
        }`}
      >
        <span className="text-[10.5px] font-semibold tracking-[0.06em] uppercase text-muted">
          {label}
        </span>
        <span>{value}</span>
        <ChevronDown />
      </MenuButton>
      <MenuItems
        anchor={{ to: 'bottom start', gap: 6 }}
        transition
        className="z-[60] min-w-[200px] origin-top-left bg-surface border border-line rounded-md shadow-elevated p-1 focus:outline-none transition duration-150 ease-out data-[closed]:opacity-0 data-[closed]:scale-95"
      >
        {options.map((opt) => (
          <MenuItem key={opt.key}>
            <button
              type="button"
              onClick={() => onSelect(opt.key)}
              className={`w-full flex items-center justify-between gap-3 text-[12.5px] px-2.5 py-1.5 rounded-sm cursor-pointer transition-colors ${
                opt.key === activeKey
                  ? 'bg-soft text-primary font-semibold'
                  : 'text-secondary data-[focus]:bg-soft data-[focus]:text-primary'
              }`}
            >
              <span>{opt.label}</span>
              <span className="text-[10.5px] tnum text-muted">{opt.count}</span>
            </button>
          </MenuItem>
        ))}
      </MenuItems>
    </Menu>
  );
}

function ChevronDown() {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}
